import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { throttle } from "lodash";
import React, { FC, useEffect, useRef } from "react";

import { getResolvedThemeColors } from "../../../styles/resolved-config";
import { MaterialType } from "../../../types/materials";
import { Cta } from "../../buttons/cta";
import { PaginationIndicator } from "../../pagination/pagination-indicator";
import {
  PaginationIndicatorColor,
  PaginationIndicatorVariant,
} from "../../pagination/pagination-indicator/types";
import { RichText } from "../../rich-text";
import { MaterialsSliderProps } from "./materials-slider";
import { MaterialsSliderImage } from "./materials-slider-image";
import { MaterialsSliderTabs, TabProps } from "./materials-slider-tabs";
import { MaterialsSliderTitle } from "./materials-slider-title";

export interface MaterialsSliderDesktopProps
  extends Pick<MaterialsSliderProps, "showTabTitle" | "slides"> {
  slideIndex: number;
  onChangeActiveTab: (activeTabKey: string) => void;
  onClickTab: (activeTabKey: string) => void;
  tabs: TabProps[];
  scrollYPercent: number;
}

export const MaterialsSliderDesktop: FC<MaterialsSliderDesktopProps> = ({
  slides,
  onChangeActiveTab,
  onClickTab,
  tabs,
  slideIndex,
  scrollYPercent,
  showTabTitle,
}) => {
  const currentSlide = slides?.[slideIndex];
  const themeColors = getResolvedThemeColors();

  // updates slideIndex depending on scroll progress
  useEffect(() => {
    // scrollYPercent can be less than zero only on initialization (used to avoid unwanted start animation)
    if (scrollYPercent < 0 || !slides) return;
    // if it's horizontal variant, we don't want to change slide on scroll
    const newSlideIndex = Math.floor(scrollYPercent * slides.length);
    const validSlideIndex = Math.max(Math.min(newSlideIndex, slides.length - 1), 0);
    const newSlideKey = slides?.[validSlideIndex]?.id;

    if (newSlideKey) onChangeActiveTab(newSlideKey);
  }, [scrollYPercent, onChangeActiveTab, slides]);

  // a particularly bored user could break the animation with a rapid succession of clicks
  const throttledOnClickTab = useRef(throttle(onClickTab, 750));

  return (
    <motion.div
      className="w-full h-[100vh] relative items-center pl-[10vw] hidden lg:flex"
      transition={{ duration: 0.5, ease: "linear" }}
      animate={{
        backgroundColor: currentSlide?.variant
          ? (themeColors[currentSlide.variant as keyof typeof themeColors] as string)
          : undefined,
      }}
    >
      {showTabTitle ? (
        <MaterialsSliderTabs
          isVertical={true}
          tabs={tabs}
          showSlide={throttledOnClickTab.current}
          activeTabKey={currentSlide?.id}
          color={currentSlide?.variant === "primary" ? "light" : "dark"}
        />
      ) : (
        <PaginationIndicator
          activeItem={slideIndex}
          color={
            currentSlide?.variant === "primary"
              ? PaginationIndicatorColor.PrimaryInverted
              : PaginationIndicatorColor.Dark
          }
          totalItems={tabs.length}
          variant={PaginationIndicatorVariant.Dots}
          onClickItem={(index) => throttledOnClickTab.current(tabs[index].key)}
          className="absolute top-0 left-14 flex-col h-full"
        />
      )}

      <div className="flex flex-1 flex-col">
        {Object.values(MaterialType).includes(currentSlide?.variant as MaterialType) && (
          <MaterialsSliderTitle material={currentSlide?.variant as MaterialType} />
        )}
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            className="h-full flex w-full flex-col justify-stretch"
            key={currentSlide?.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { ease: "easeOut", duration: 0.4 },
            }}
          >
            {currentSlide && (
              <div className="flex flex-col gap-2">
                {!Object.values(MaterialType).includes(currentSlide?.variant as MaterialType) &&
                  currentSlide?.title && (
                    <div className="text-light text-5xl font-medium leading-tight whitespace-break-spaces mb-14">
                      {currentSlide?.title}
                    </div>
                  )}
                <div
                  className={clsx(
                    "text-xl 2xl:text-2xl w-full !leading-relaxed text-dark font-light",
                    currentSlide?.variant === "primary" && "text-light"
                  )}
                >
                  {currentSlide?.description?.raw && (
                    <RichText raw={currentSlide.description.raw} />
                  )}
                </div>
                {currentSlide?.cta && (
                  <div className="pt-8">
                    <Cta
                      {...currentSlide.cta}
                      variant={currentSlide?.variant === "primary" ? "light" : "neutral"}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div
        className={clsx(
          "h-full w-full overflow-hidden p-0 m-0",
          "flex justify-center items-center relative flex-1"
        )}
      >
        {slides?.map((slide, index) => (
          <MaterialsSliderImage
            key={index}
            page={index}
            currentSlideIndex={slideIndex}
            source={slide?.image?.gatsbyImageData}
          />
        ))}
      </div>
    </motion.div>
  );
};
